import { useHistory, useLocation } from 'react-router';
import { useSelector } from 'react-redux';
import { AppState } from '../../store/appState';
import { RefObject, useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAutomaticSpocSwitch } from '../../store/authentication/actions';

interface SetSearchParamEvent {
    version: number;
    paramName: string;
    newValue: string | undefined;
}

interface NavigateToEvent {
    version: number;
    pathName: string;
    searchParams?: string;
}

const EVENT_SET_QUERY_PARAM = 'setQueryParam';
const EVENT_NAVIGATE_TO = 'navigateTo';
const EVENT_TOKEN_CHANGED = 'tokenChanged';

const isNavigateToEventV1 = (arg: any): arg is NavigateToEvent => {
    return arg.version === 1 && arg.pathName !== undefined;
};

const isSetQueryParamV1 = (arg: any): arg is SetSearchParamEvent => {
    return arg.version === 1 && arg.paramName !== undefined;
};

function isCustomEvent(arg: any): arg is CustomEvent {
    return arg.detail !== undefined;
}

function dispatchTokenEvent(customElementRef: RefObject<HTMLDivElement>) {
    const event = new CustomEvent(EVENT_TOKEN_CHANGED, {
        detail: {
            version: 1,
            token: localStorage.getItem('token')
        }
    });

    customElementRef.current?.dispatchEvent(event);
}

export const ReportingPage = () => {
    const { i18n } = useTranslation();
    // replace with react router v6 useSearchParam when upgrading
    const [searchParams, setSearchParams] = useSearchParams();
    const { push } = useHistory();
    const location = useLocation();
    const spoc = useSelector((state: AppState) => state.authentication.spoc);
    const customElementRef = useRef<HTMLDivElement>(null);

    // this hook is responsible for switching the spoc according to the search param "spocId"
    useAutomaticSpocSwitch();

    useEffect(() => {
        const handleNavigateToEvent = (event: any) => {
            if (isCustomEvent(event) && isNavigateToEventV1(event.detail)) {
                push(event.detail.pathName, event.detail.searchParams);
            }
        };

        const handleSetSearchParamEvent = (event: any) => {
            if (isCustomEvent(event) && isSetQueryParamV1(event.detail)) {
                if (event.detail.newValue !== undefined) {
                    searchParams.set(event.detail.paramName, event.detail.newValue);
                } else {
                    searchParams.delete(event.detail.paramName);
                }
                setSearchParams(searchParams);
            }
        };

        setInterval(() => {
            dispatchTokenEvent(customElementRef);
        }, 3000);
        dispatchTokenEvent(customElementRef);
        const currentElement = customElementRef.current;
        currentElement && currentElement.addEventListener(EVENT_NAVIGATE_TO, handleNavigateToEvent);
        currentElement && currentElement.addEventListener(EVENT_SET_QUERY_PARAM, handleSetSearchParamEvent);

        return () => {
            currentElement && currentElement.removeEventListener(EVENT_NAVIGATE_TO, handleNavigateToEvent);
            currentElement && currentElement.removeEventListener(EVENT_SET_QUERY_PARAM, handleSetSearchParamEvent);
        };
    }, [searchParams, push, setSearchParams]);

    const partnerId = (spoc.id.startsWith('PARTNER') ? spoc.id : spoc?.partnerId)?.replace('PARTNER', '');
    const siteId = spoc.id.startsWith('SITE') ? spoc.id.replace('SITE', '') : undefined;

    return (
        <div className="min-h-screen">
            <business-intelligence
                ref={customElementRef}
                pathname={location.pathname}
                searchparams={searchParams.toString()}
                partnerId={partnerId}
                siteId={siteId}
                language={i18n.language}
            ></business-intelligence>
        </div>
    );
};

function createSearchParams(init: URLSearchParamsInit = ''): URLSearchParams {
    return new URLSearchParams(
        typeof init === 'string' || Array.isArray(init) || init instanceof URLSearchParams
            ? init
            : Object.keys(init).reduce((memo, key) => {
                  let value = init[key];
                  return memo.concat(Array.isArray(value) ? value.map(v => [key, v]) : [[key, value]]);
              }, [] as ParamKeyValuePair[])
    );
}

type ParamKeyValuePair = [string, string];
type SetURLSearchParams = (
    nextInit?: URLSearchParamsInit | ((prev: URLSearchParams) => URLSearchParamsInit),
    navigateOpts?: any
) => void;
type URLSearchParamsInit = string | ParamKeyValuePair[] | Record<string, string | string[]> | URLSearchParams;

function getSearchParamsForLocation(locationSearch: string, defaultSearchParams: URLSearchParams | null) {
    let searchParams = createSearchParams(locationSearch);

    if (defaultSearchParams) {
        for (let key of defaultSearchParams.keys()) {
            if (!searchParams.has(key)) {
                defaultSearchParams.getAll(key).forEach(value => {
                    searchParams.append(key, value);
                });
            }
        }
    }

    return searchParams;
}

function useSearchParams(defaultInit?: URLSearchParamsInit): [URLSearchParams, SetURLSearchParams] {
    let defaultSearchParamsRef = useRef(createSearchParams(defaultInit));
    let hasSetSearchParamsRef = useRef(false);

    let location = useLocation();
    let searchParams = useMemo(
        () =>
            // Only merge in the defaults if we haven't yet called setSearchParams.
            // Once we call that we want those to take precedence, otherwise you can't
            // remove a param with setSearchParams({}) if it has an initial value
            getSearchParamsForLocation(
                location.search,
                hasSetSearchParamsRef.current ? null : defaultSearchParamsRef.current
            ),
        [location.search]
    );

    let { replace } = useHistory();
    let setSearchParams = useCallback<SetURLSearchParams>(
        (nextInit, navigateOptions) => {
            const newSearchParams = createSearchParams(
                typeof nextInit === 'function' ? nextInit(searchParams) : nextInit
            );
            hasSetSearchParamsRef.current = true;
            replace({
                search: newSearchParams.toString()
            });
        },
        [replace, searchParams]
    );

    return [searchParams, setSearchParams];
}
